import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  BuildingOffice2Icon,
  UserCircleIcon,
  CalendarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';

const EXPIRING_DAYS = 30;

type CardFilter = 'expiring_soon' | 'low_licenses' | null;
type SortBy = 'start' | 'end' | 'available' | null;
type SortDir = 'asc' | 'desc';

const formatEarliestExpiry = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
};

const AdministrationSubscriptions: React.FC = () => {
  const { t } = useTranslation('subscriptions');
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageTitle(t('pageTitle'));

  const [cardFilter, setCardFilter] = useState<CardFilter>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortBy>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['sysadmin-metrics'],
    queryFn: () => apiService.getSysadminMetrics(),
    enabled: user?.role === 'sysadmin',
  });

  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ['sysadmin-subscriptions'],
    queryFn: () => apiService.getSysadminSubscriptions(),
    enabled: user?.role === 'sysadmin',
  });

  const subscriptions = useMemo(() => subsData?.subscriptions ?? [], [subsData]);
  const isLoading = metricsLoading || subsLoading;

  const filteredSubscriptions = useMemo(() => {
    const now = new Date();
    const expiringCutoff = new Date(now.getTime() + EXPIRING_DAYS * 24 * 60 * 60 * 1000);
    return subscriptions.filter((row: {
      subscription: Record<string, unknown>;
      available_full_licenses?: number;
      earliest_expiry?: string;
    }) => {
      const sub = row.subscription as Record<string, unknown>;
      const entityType = (sub.entity_type as string) ?? '';
      const status = (sub.status as string) ?? '';

      if (statusFilter !== 'all' && status !== statusFilter) return false;

      if (cardFilter === 'expiring_soon') {
        if (!row.earliest_expiry) return false;
        const expiry = new Date(row.earliest_expiry);
        return expiry > now && expiry < expiringCutoff;
      }
      if (cardFilter === 'low_licenses') {
        return entityType === 'company' && row.available_full_licenses === 0;
      }
      return true;
    });
  }, [subscriptions, cardFilter, statusFilter]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    subscriptions.forEach((row: { subscription: Record<string, unknown> }) => {
      const s = (row.subscription as Record<string, unknown>).status as string;
      if (s) set.add(s);
    });
    return ['all', ...Array.from(set).sort()];
  }, [subscriptions]);

  const sortedSubscriptions = useMemo(() => {
    if (!sortBy) return filteredSubscriptions;
    const rows = [...filteredSubscriptions] as Array<{
      subscription: Record<string, unknown>;
      entity_name?: string;
      available_full_licenses?: number;
      earliest_expiry?: string;
    }>;
    const mult = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortBy === 'available') {
        const av = (x: typeof a) => (x.available_full_licenses != null ? x.available_full_licenses : -1);
        return mult * (av(a) - av(b));
      }
      if (sortBy === 'start') {
        const ts = (x: typeof a) => (x.subscription?.created_at ? new Date(x.subscription.created_at as string).getTime() : 0);
        return mult * (ts(a) - ts(b));
      }
      if (sortBy === 'end') {
        const ts = (x: typeof a) => (x.earliest_expiry ? new Date(x.earliest_expiry).getTime() : 0);
        return mult * (ts(a) - ts(b));
      }
      return 0;
    });
    return rows;
  }, [filteredSubscriptions, sortBy, sortDir]);

  const handleSort = (col: SortBy) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  if (!user) return null;
  if (user.role !== 'sysadmin') {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
        </div>
      )}

      {!isLoading && metrics && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <button
              type="button"
              onClick={() => setCardFilter(null)}
              className={`rounded-lg border p-4 text-left transition ${cardFilter === null ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('totalSubscriptions')}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{metrics.total_subscriptions}</p>
            </button>
            <button
              type="button"
              onClick={() => setCardFilter('expiring_soon')}
              className={`rounded-lg border p-4 text-left transition ${cardFilter === 'expiring_soon' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('expiringWithinDays', { days: metrics.expiring_soon_days })}</p>
              <p className="mt-1 text-2xl font-semibold text-amber-600 dark:text-amber-400">{metrics.expiring_soon_count}</p>
            </button>
            <button
              type="button"
              onClick={() => setCardFilter('low_licenses')}
              className={`rounded-lg border p-4 text-left transition ${cardFilter === 'low_licenses' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('lowLicenses')}</p>
              <p className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">{metrics.low_licenses_count}</p>
            </button>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('allSubscriptions')}</h2>
              <div className="flex items-center gap-2">
                <label htmlFor="status-filter" className="text-xs text-gray-500 dark:text-gray-400">{t('status')}</label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="min-w-[12rem] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white px-3 py-1.5 pr-8"
                >
                  {statusOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt === 'all' ? t('all') : opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('entity')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('status')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSort('available'); }}
                        className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {t('available')}
                        {sortBy === 'available' ? (
                          sortDir === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowsUpDownIcon className="w-4 h-4 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSort('start'); }}
                        className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {t('start')}
                        {sortBy === 'start' ? (
                          sortDir === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowsUpDownIcon className="w-4 h-4 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSort('end'); }}
                        className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {t('end')}
                        {sortBy === 'end' ? (
                          sortDir === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowsUpDownIcon className="w-4 h-4 opacity-50" />
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedSubscriptions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('noSubscriptionsMatch')}
                      </td>
                    </tr>
                  )}
                  {sortedSubscriptions.map((row: {
                    subscription: Record<string, unknown>;
                    entity_name?: string;
                    available_full_licenses?: number;
                    earliest_expiry?: string;
                  }) => {
                    const sub = row.subscription as Record<string, unknown>;
                    const subId = (sub.id as string) ?? '';
                    const entityType = (sub.entity_type as string) ?? '';
                    const entityId = (sub.entity_id as string) ?? '';
                    const status = (sub.status as string) ?? '';
                    const displayName = row.entity_name || entityId;
                    const isCompany = entityType === 'company';
                    const detailHref = `/administration/subscriptions/${subId}`;
                    return (
                      <tr
                        key={subId}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(detailHref)}
                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(detailHref); }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isCompany ? (
                              <BuildingOffice2Icon className="w-5 h-5 text-indigo-500 shrink-0" aria-label="Company" />
                            ) : (
                              <UserCircleIcon className="w-5 h-5 text-gray-500 shrink-0" aria-label="User" />
                            )}
                            <span>{displayName || entityId}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{status}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {row.available_full_licenses != null ? row.available_full_licenses : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 font-mono">
                          {sub.created_at ? (
                            <span className="inline-flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4 shrink-0" />
                              {formatEarliestExpiry(sub.created_at as string)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 font-mono">
                          {row.earliest_expiry ? (
                            <span className="inline-flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4 shrink-0" />
                              {formatEarliestExpiry(row.earliest_expiry)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdministrationSubscriptions;
